/** The four cues share the reference's marimba voice and generated room. */
type Voice = { ratio: number; index: number; cutoff: number; send: number };
type CueEvent = { t: number; dur: number } & (
  | { kind: "mallet"; f: number; gain?: number; voice?: Partial<Voice> }
  | { kind: "knock"; f: number; q: number; gain: number; filter?: BiquadFilterType; send?: number }
  | { kind: "thud"; f0: number; f1: number; gain: number }
  | { kind: "ring"; f: number; partial: number; gain: number }
);
interface Cue {
  loop: boolean;
  period: number;
  events: CueEvent[];
}

const MARIMBA = { ratio: 3.0, index: 3.5, cutoff: 1900, send: 0.2 };

let ctx: AudioContext | undefined;
let dry: GainNode;
let wet: GainNode;
let noiseBuf: AudioBuffer;
let scheduled: AudioScheduledSourceNode[] = [];

/**
 * MUST be first called from inside a real user gesture handler, synchronously.
 * A context created after an `await` is born suspended and never plays.
 */
function audio(): AudioContext {
  if (!ctx) {
    ctx = new AudioContext();
    dry = ctx.createGain();
    dry.gain.value = 0.9;
    dry.connect(ctx.destination);

    // Generated impulse response: noise decaying exponentially. This is the
    // difference between "an instrument in a room" and "a tone from a speaker".
    const rate = ctx.sampleRate;
    const irLen = Math.floor(rate * 1.6);
    const ir = ctx.createBuffer(2, irLen, rate);
    for (let ch = 0; ch < 2; ch++) {
      const d = ir.getChannelData(ch);
      for (let i = 0; i < irLen; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / irLen) ** 2.6;
    }
    const conv = ctx.createConvolver();
    conv.buffer = ir;
    wet = ctx.createGain();
    wet.gain.value = 1;
    wet.connect(conv).connect(ctx.destination);

    // One second of white noise, reused by every strike.
    noiseBuf = ctx.createBuffer(1, rate, rate);
    const nd = noiseBuf.getChannelData(0);
    for (let i = 0; i < rate; i++) nd[i] = Math.random() * 2 - 1;
  }
  if (ctx.state === "suspended")
    void ctx.resume().catch(() => {
      /* A later gesture can retry a browser refusal. */
    });
  return ctx;
}

function route(ac: AudioContext, node: AudioNode, send: number) {
  const s = ac.createGain();
  s.gain.value = send;
  node.connect(dry);
  node.connect(s).connect(wet);
}

/** FM mallet. Modulation depth decays faster than the note: wooden at the strike. */
function mallet(ac: AudioContext, v: Voice, freq: number, when: number, dur: number, peak: number) {
  const car = ac.createOscillator();
  car.type = "sine";
  car.frequency.value = freq;
  const mod = ac.createOscillator();
  mod.type = "sine";
  mod.frequency.value = freq * v.ratio;

  const modGain = ac.createGain();
  modGain.gain.setValueAtTime(freq * v.index, when);
  modGain.gain.exponentialRampToValueAtTime(freq * 0.02, when + dur * 0.4);
  mod.connect(modGain).connect(car.frequency);

  const env = ac.createGain();
  env.gain.setValueAtTime(0.0001, when);
  env.gain.exponentialRampToValueAtTime(peak, when + 0.01);
  env.gain.exponentialRampToValueAtTime(0.0001, when + dur);

  const lp = ac.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = v.cutoff;

  car.connect(env).connect(lp);
  route(ac, lp, v.send);
  scheduled.push(car, mod);
  car.start(when);
  car.stop(when + dur + 0.1);
  mod.start(when);
  mod.stop(when + dur + 0.1);
}

/** Filtered noise: high Q rings like struck wood, low Q reads as plastic or felt. */
function knock(
  ac: AudioContext,
  when: number,
  freq: number,
  q: number,
  dur: number,
  peak: number,
  filter?: BiquadFilterType,
  send?: number,
) {
  const src = ac.createBufferSource();
  src.buffer = noiseBuf;
  const bp = ac.createBiquadFilter();
  bp.type = filter || "bandpass";
  bp.frequency.value = freq;
  bp.Q.value = q;

  const env = ac.createGain();
  env.gain.setValueAtTime(peak, when);
  env.gain.exponentialRampToValueAtTime(0.0001, when + dur);

  src.connect(bp).connect(env);
  route(ac, env, send === undefined ? 0.22 : send);
  scheduled.push(src);
  src.start(when);
  src.stop(when + dur + 0.05);
}

/** A fast downward slide is what a falling object sounds like. */
function thud(ac: AudioContext, when: number, f0: number, f1: number, dur: number, peak: number) {
  const o = ac.createOscillator();
  o.type = "sine";
  o.frequency.setValueAtTime(f0, when);
  o.frequency.exponentialRampToValueAtTime(f1, when + Math.min(dur * 0.55, 0.18));

  const env = ac.createGain();
  env.gain.setValueAtTime(0.0001, when);
  env.gain.exponentialRampToValueAtTime(peak, when + 0.006);
  env.gain.exponentialRampToValueAtTime(0.0001, when + dur);

  o.connect(env);
  route(ac, env, 0.18);
  scheduled.push(o);
  o.start(when);
  o.stop(when + dur + 0.05);
}

/**
 * What keeps a cue alive after the strike. The partial is deliberately NOT a
 * whole-number multiple: an inharmonic pair rings like a struck object, while
 * an octave or a fifth would just sound like a chord being held.
 */
function resonance(ac: AudioContext, when: number, freq: number, partial: number, dur: number, peak: number) {
  const env = ac.createGain();
  env.gain.setValueAtTime(0.0001, when);
  env.gain.exponentialRampToValueAtTime(peak, when + 0.012);
  env.gain.exponentialRampToValueAtTime(0.0001, when + dur);

  const lp = ac.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 2400;

  (
    [
      [freq, 1],
      [freq * partial, 0.34],
    ] as const
  ).forEach(([f, amt]) => {
    const o = ac.createOscillator();
    o.type = "sine";
    o.frequency.value = f;
    const g = ac.createGain();
    g.gain.value = amt;
    o.connect(g).connect(env);
    scheduled.push(o);
    o.start(when);
    o.stop(when + dur + 0.1);
  });

  env.connect(lp);
  route(ac, lp, 0.34);
}

/* ------------------------------------------------------------------ */
/* The four cues. `t` is seconds from the start of the phrase.          */
/* ------------------------------------------------------------------ */

// Waiting on the rules about when a clinician may be interrupted.
/** 1. INCOMING RING — "Answer me". Loops every 3.2 s. DO NOT WIRE YET. */
export const ANSWER_ME: Cue = {
  loop: true,
  period: 3.2,
  events: [
    { t: 0, kind: "mallet", f: 329.63, dur: 1.2 },
    { t: 0.13, kind: "mallet", f: 440.0, dur: 1.2 },
    { t: 0.26, kind: "mallet", f: 554.37, dur: 1.2 },
    { t: 0.42, kind: "mallet", f: 659.25, dur: 1.3 },
  ],
};

/** 2. OUTGOING RINGBACK — "Marimba lift". Loops every 3.8 s. */
export const MARIMBA_LIFT: Cue = {
  loop: true,
  period: 3.8,
  events: [
    { t: 0, kind: "mallet", f: 220.0, dur: 1.1 },
    { t: 0.12, kind: "mallet", f: 329.63, dur: 1.1 },
    { t: 0.32, kind: "mallet", f: 440.0, dur: 1.1 },
    { t: 0.46, kind: "mallet", f: 493.88, dur: 1.1 },
  ],
};

/** 3. DECLINED — "Damped fall, hard". Plays once, about 0.7 s of sound. */
export const DAMPED_FALL: Cue = {
  loop: false,
  period: 1.8,
  events: [
    { t: 0, kind: "knock", f: 1500, q: 1.5, dur: 0.06, gain: 0.26 },
    { t: 0, kind: "mallet", f: 523.25, dur: 0.3 },
    { t: 0.14, kind: "knock", f: 1200, q: 1.5, dur: 0.06, gain: 0.24 },
    { t: 0.14, kind: "mallet", f: 440.0, dur: 0.5 },
    { t: 0.14, kind: "ring", f: 220, partial: 2.6, dur: 0.7, gain: 0.09 },
  ],
};

/** 4. HANG UP — "Receiver down". Plays once, about 0.26 s of sound. */
export const RECEIVER_DOWN: Cue = {
  loop: false,
  period: 1.2,
  events: [
    { t: 0, kind: "knock", f: 2600, q: 1.0, dur: 0.012, gain: 0.4, send: 0.1 },
    { t: 0, kind: "thud", f0: 420, f1: 260, dur: 0.1, gain: 0.2 },
    { t: 0.075, kind: "knock", f: 1800, q: 1.2, dur: 0.02, gain: 0.34, send: 0.1 },
    { t: 0.075, kind: "thud", f0: 300, f1: 180, dur: 0.18, gain: 0.22 },
    { t: 0.075, kind: "ring", f: 180, partial: 2.3, dur: 0.45, gain: 0.07 },
  ],
};

/** Schedules one pass of a cue. Loops are driven by repeating this at `period`. */
export function playOnce(cue: Cue): () => void {
  // Notifications cannot unlock audio. Only the call and answer gestures do.
  const ac = ctx;
  if (!ac || ac.state !== "running") return () => {};
  scheduled = [];
  const t = ac.currentTime + 0.04;
  for (const e of cue.events) {
    if (e.kind === "mallet")
      mallet(ac, Object.assign({}, MARIMBA, e.voice || {}), e.f, t + e.t, e.dur, e.gain || 0.085);
    else if (e.kind === "knock") knock(ac, t + e.t, e.f, e.q, e.dur, e.gain, e.filter, e.send);
    else if (e.kind === "thud") thud(ac, t + e.t, e.f0, e.f1, e.dur, e.gain);
    else if (e.kind === "ring") resonance(ac, t + e.t, e.f, e.partial, e.dur, e.gain);
  }
  const sources = scheduled;
  return () => {
    // Cancel future strikes as well as the note already sounding. The room's
    // natural reverb tail may decay; no new part of the phrase may begin.
    for (const source of sources) source.stop();
  };
}

/** Call synchronously from a gesture, before a network await spends it. */
export function prepareCallAudio(): void {
  // A global unlock listener belongs here once incoming interruption rules
  // are settled. Outgoing calls and answers already supply their own gesture.
  if (typeof AudioContext === "undefined") return;
  try {
    audio();
  } catch (error) {
    // Sound is confirmation; an unavailable audio device must not block a call.
    console.warn("[ColleagueCall] audio unavailable", error);
  }
}
